import React, { useState } from 'react';
import { BellIcon, ShieldIcon, KeyIcon, GlobeIcon, CreditCardIcon, UserIcon, MailIcon, SmartphoneIcon, CheckIcon, XIcon, PlusIcon, TrashIcon, EyeIcon, EyeOffIcon, AlertCircleIcon } from 'lucide-react';
type TabType = 'profile' | 'billing' | 'privacy' | 'security' | 'notifications';
export const Settings = () => {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  // Profile state
  const [profileData, setProfileData] = useState({
    displayName: 'John Doe',
    email: 'john@example.com',
    phone: '+1 (555) 123-4567',
    bio: 'AI enthusiast and developer',
    location: 'San Francisco, CA',
    website: 'https://johndoe.com'
  });
  // Privacy state
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showApps: true,
    allowMessages: true,
    dataSharing: false,
    analytics: true
  });
  // Security state
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    loginAlerts: true,
    sessionTimeout: '30'
  });
  // Notification state
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    appUpdates: true,
    newFeatures: true,
    marketingEmails: false,
    weeklyDigest: true,
    securityAlerts: true
  });
  const tabs = [{
    id: 'profile' as TabType,
    label: 'Profile',
    icon: <UserIcon className="h-5 w-5" data-id="element-1739" />
  }, {
    id: 'billing' as TabType,
    label: 'Billing',
    icon: <CreditCardIcon className="h-5 w-5" data-id="element-1740" />
  }, {
    id: 'privacy' as TabType,
    label: 'Privacy',
    icon: <ShieldIcon className="h-5 w-5" data-id="element-1741" />
  }, {
    id: 'security' as TabType,
    label: 'Security',
    icon: <KeyIcon className="h-5 w-5" data-id="element-1742" />
  }, {
    id: 'notifications' as TabType,
    label: 'Notifications',
    icon: <BellIcon className="h-5 w-5" data-id="element-1743" />
  }];
  const paymentMethods = [{
    id: '1',
    type: 'Visa',
    last4: '4242',
    expiry: '12/25',
    isDefault: true
  }, {
    id: '2',
    type: 'Mastercard',
    last4: '8888',
    expiry: '09/24',
    isDefault: false
  }];
  const billingHistory = [{
    id: '1',
    date: 'June 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-001'
  }, {
    id: '2',
    date: 'May 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-002'
  }, {
    id: '3',
    date: 'April 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-003'
  }];
  const activeSessions = [{
    id: '1',
    device: 'Chrome on MacBook Pro',
    location: 'San Francisco, CA',
    lastActive: '2 minutes ago',
    current: true
  }, {
    id: '2',
    device: 'Safari on iPhone 14',
    location: 'San Francisco, CA',
    lastActive: '1 hour ago',
    current: false
  }];
  return <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8" data-id="element-1744">
      <div className="max-w-6xl mx-auto" data-id="element-1745">
        <h1 className="text-2xl font-bold mb-8" data-id="element-1746">Settings</h1>
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-1747">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0" data-id="element-1748">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1749">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center px-4 py-3 border-b border-gray-200 last:border-b-0 transition-colors ${activeTab === tab.id ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1750">
                  {tab.icon}
                  <span className="ml-3 font-medium" data-id="element-1751">{tab.label}</span>
                </button>)}
            </div>
          </div>
          {/* Content Area */}
          <div className="flex-1" data-id="element-1752">
            {/* Profile Tab */}
            {activeTab === 'profile' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1753">
                <h2 className="text-xl font-semibold mb-6" data-id="element-1754">Profile Settings</h2>
                <div className="space-y-6" data-id="element-1755">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1756">
                    <div data-id="element-1757">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1758">
                        Display Name
                      </label>
                      <input type="text" value={profileData.displayName} onChange={e => setProfileData({
                    ...profileData,
                    displayName: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1759" />
                    </div>
                    <div data-id="element-1760">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1761">
                        Email Address
                      </label>
                      <input type="email" value={profileData.email} onChange={e => setProfileData({
                    ...profileData,
                    email: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1762" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1763">
                    <div data-id="element-1764">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1765">
                        Phone Number
                      </label>
                      <input type="tel" value={profileData.phone} onChange={e => setProfileData({
                    ...profileData,
                    phone: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1766" />
                    </div>
                    <div data-id="element-1767">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1768">
                        Location
                      </label>
                      <input type="text" value={profileData.location} onChange={e => setProfileData({
                    ...profileData,
                    location: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1769" />
                    </div>
                  </div>
                  <div data-id="element-1770">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1771">
                      Website
                    </label>
                    <input type="url" value={profileData.website} onChange={e => setProfileData({
                  ...profileData,
                  website: e.target.value
                })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1772" />
                  </div>
                  <div data-id="element-1773">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1774">
                      Bio
                    </label>
                    <textarea value={profileData.bio} onChange={e => setProfileData({
                  ...profileData,
                  bio: e.target.value
                })} rows={4} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1775" />
                  </div>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-1776">
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1777">
                      Cancel
                    </button>
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1778">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
            {/* Billing Tab */}
            {activeTab === 'billing' && <div className="space-y-6" data-id="element-1779">
                {/* Payment Methods */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1780">
                  <div className="flex justify-between items-center mb-6" data-id="element-1781">
                    <h2 className="text-xl font-semibold" data-id="element-1782">Payment Methods</h2>
                    <button className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1783">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-1784" />
                      Add Method
                    </button>
                  </div>
                  <div className="space-y-4" data-id="element-1785">
                    {paymentMethods.map(method => <div key={method.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-1786">
                        <div className="flex items-center" data-id="element-1787">
                          <CreditCardIcon className="h-6 w-6 text-gray-400 mr-4" data-id="element-1788" />
                          <div data-id="element-1789">
                            <div className="font-medium" data-id="element-1790">
                              {method.type} •••• {method.last4}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1791">
                              Expires {method.expiry}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2" data-id="element-1792">
                          {method.isDefault && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-1793">
                              Default
                            </span>}
                          <button className="p-2 text-gray-400 hover:text-red-600" data-id="element-1794">
                            <TrashIcon className="h-4 w-4" data-id="element-1795" />
                          </button>
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Billing History */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1796">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1797">
                    Billing History
                  </h2>
                  <div className="overflow-x-auto" data-id="element-1798">
                    <table className="w-full" data-id="element-1799">
                      <thead data-id="element-1800">
                        <tr className="border-b border-gray-200" data-id="element-1801">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1802">
                            Date
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1803">
                            Amount
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1804">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1805">
                            Invoice
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-700" data-id="element-1806">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-1807">
                        {billingHistory.map(item => <tr key={item.id} className="border-b border-gray-100" data-id="element-1808">
                            <td className="py-3 px-4 text-sm" data-id="element-1809">{item.date}</td>
                            <td className="py-3 px-4 text-sm font-medium" data-id="element-1810">
                              {item.amount}
                            </td>
                            <td className="py-3 px-4" data-id="element-1811">
                              <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-1812">
                                {item.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500" data-id="element-1813">
                              {item.invoice}
                            </td>
                            <td className="py-3 px-4 text-right" data-id="element-1814">
                              <button className="text-sm text-indigo-600 hover:text-indigo-700" data-id="element-1815">
                                Download
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>}
            {/* Privacy Tab */}
            {activeTab === 'privacy' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1816">
                <h2 className="text-xl font-semibold mb-6" data-id="element-1817">Privacy Settings</h2>
                <div className="space-y-6" data-id="element-1818">
                  <div data-id="element-1819">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1820">
                      Profile Visibility
                    </label>
                    <select value={privacySettings.profileVisibility} onChange={e => setPrivacySettings({
                  ...privacySettings,
                  profileVisibility: e.target.value
                })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1821">
                      <option value="public" data-id="element-1822">Public</option>
                      <option value="private" data-id="element-1823">Private</option>
                      <option value="friends" data-id="element-1824">Friends Only</option>
                    </select>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-1825">
                      Control who can see your profile
                    </p>
                  </div>
                  <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-1826">
                    <label className="flex items-center justify-between" data-id="element-1827">
                      <div data-id="element-1828">
                        <div className="font-medium" data-id="element-1829">Show Email Address</div>
                        <div className="text-sm text-gray-500" data-id="element-1830">
                          Display your email on your profile
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    showEmail: !privacySettings.showEmail
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.showEmail ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1831">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.showEmail ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1832" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-1833">
                      <div data-id="element-1834">
                        <div className="font-medium" data-id="element-1835">Show My Apps</div>
                        <div className="text-sm text-gray-500" data-id="element-1836">
                          Display your created apps publicly
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    showApps: !privacySettings.showApps
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.showApps ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1837">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.showApps ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1838" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-1839">
                      <div data-id="element-1840">
                        <div className="font-medium" data-id="element-1841">Allow Messages</div>
                        <div className="text-sm text-gray-500" data-id="element-1842">
                          Let other users send you messages
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    allowMessages: !privacySettings.allowMessages
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.allowMessages ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1843">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.allowMessages ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1844" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-1845">
                      <div data-id="element-1846">
                        <div className="font-medium" data-id="element-1847">Data Sharing</div>
                        <div className="text-sm text-gray-500" data-id="element-1848">
                          Share usage data to improve services
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    dataSharing: !privacySettings.dataSharing
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.dataSharing ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1849">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.dataSharing ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1850" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-1851">
                      <div data-id="element-1852">
                        <div className="font-medium" data-id="element-1853">Analytics</div>
                        <div className="text-sm text-gray-500" data-id="element-1854">
                          Help us improve with anonymous analytics
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    analytics: !privacySettings.analytics
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.analytics ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1855">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.analytics ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1856" />
                      </button>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-1857">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1858">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
            {/* Security Tab */}
            {activeTab === 'security' && <div className="space-y-6" data-id="element-1859">
                {/* Change Password */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1860">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1861">
                    Change Password
                  </h2>
                  <div className="space-y-4" data-id="element-1862">
                    <div data-id="element-1863">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1864">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-1865">
                        <input type={showPassword ? 'text' : 'password'} className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1866" />
                        <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-gray-400" data-id="element-1867">
                          {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1868" /> : <EyeIcon className="h-5 w-5" data-id="element-1869" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-1870">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1871">
                        New Password
                      </label>
                      <div className="relative" data-id="element-1872">
                        <input type={showNewPassword ? 'text' : 'password'} className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1873" />
                        <button onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-2.5 text-gray-400" data-id="element-1874">
                          {showNewPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1875" /> : <EyeIcon className="h-5 w-5" data-id="element-1876" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-1877">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1878">
                        Confirm New Password
                      </label>
                      <input type="password" className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1879" />
                    </div>
                    <div className="flex justify-end pt-2" data-id="element-1880">
                      <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1881">
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>
                {/* Two-Factor Authentication */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1882">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1883">
                    Two-Factor Authentication
                  </h2>
                  <div className="space-y-4" data-id="element-1884">
                    <label className="flex items-center justify-between" data-id="element-1885">
                      <div data-id="element-1886">
                        <div className="font-medium" data-id="element-1887">Enable 2FA</div>
                        <div className="text-sm text-gray-500" data-id="element-1888">
                          Add an extra layer of security to your account
                        </div>
                      </div>
                      <button onClick={() => setSecuritySettings({
                    ...securitySettings,
                    twoFactorEnabled: !securitySettings.twoFactorEnabled
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.twoFactorEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1889">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1890" />
                      </button>
                    </label>
                    {securitySettings.twoFactorEnabled && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1891">
                        <div className="flex items-start" data-id="element-1892">
                          <AlertCircleIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5" data-id="element-1893" />
                          <div data-id="element-1894">
                            <p className="text-sm text-blue-900 font-medium" data-id="element-1895">
                              2FA is enabled
                            </p>
                            <p className="text-sm text-blue-700 mt-1" data-id="element-1896">
                              You will need to enter a verification code when
                              logging in
                            </p>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
                {/* Security Preferences */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1897">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1898">
                    Security Preferences
                  </h2>
                  <div className="space-y-4" data-id="element-1899">
                    <label className="flex items-center justify-between" data-id="element-1900">
                      <div data-id="element-1901">
                        <div className="font-medium" data-id="element-1902">Login Alerts</div>
                        <div className="text-sm text-gray-500" data-id="element-1903">
                          Get notified of new login attempts
                        </div>
                      </div>
                      <button onClick={() => setSecuritySettings({
                    ...securitySettings,
                    loginAlerts: !securitySettings.loginAlerts
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.loginAlerts ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1904">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.loginAlerts ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1905" />
                      </button>
                    </label>
                    <div data-id="element-1906">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1907">
                        Session Timeout
                      </label>
                      <select value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
                    ...securitySettings,
                    sessionTimeout: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1908">
                        <option value="15" data-id="element-1909">15 minutes</option>
                        <option value="30" data-id="element-1910">30 minutes</option>
                        <option value="60" data-id="element-1911">1 hour</option>
                        <option value="120" data-id="element-1912">2 hours</option>
                      </select>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-1913">
                        Automatically log out after inactivity
                      </p>
                    </div>
                  </div>
                </div>
                {/* Active Sessions */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1914">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-1915">
                    Active Sessions
                  </h2>
                  <div className="space-y-4" data-id="element-1916">
                    {activeSessions.map(session => <div key={session.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-1917">
                        <div className="flex items-center" data-id="element-1918">
                          <SmartphoneIcon className="h-6 w-6 text-gray-400 mr-4" data-id="element-1919" />
                          <div data-id="element-1920">
                            <div className="font-medium flex items-center" data-id="element-1921">
                              {session.device}
                              {session.current && <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-1922">
                                  Current
                                </span>}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1923">
                              {session.location} • {session.lastActive}
                            </div>
                          </div>
                        </div>
                        {!session.current && <button className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md" data-id="element-1924">
                            Revoke
                          </button>}
                      </div>)}
                  </div>
                </div>
              </div>}
            {/* Notifications Tab */}
            {activeTab === 'notifications' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1925">
                <h2 className="text-xl font-semibold mb-6" data-id="element-1926">
                  Notification Preferences
                </h2>
                <div className="space-y-4" data-id="element-1927">
                  <label className="flex items-center justify-between" data-id="element-1928">
                    <div data-id="element-1929">
                      <div className="font-medium" data-id="element-1930">Email Notifications</div>
                      <div className="text-sm text-gray-500" data-id="element-1931">
                        Receive notifications via email
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  emailNotifications: !notificationSettings.emailNotifications
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.emailNotifications ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1932">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.emailNotifications ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1933" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1934">
                    <div data-id="element-1935">
                      <div className="font-medium" data-id="element-1936">App Updates</div>
                      <div className="text-sm text-gray-500" data-id="element-1937">
                        Get notified when your apps are updated
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  appUpdates: !notificationSettings.appUpdates
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.appUpdates ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1938">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.appUpdates ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1939" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1940">
                    <div data-id="element-1941">
                      <div className="font-medium" data-id="element-1942">New Features</div>
                      <div className="text-sm text-gray-500" data-id="element-1943">
                        Learn about new platform features
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  newFeatures: !notificationSettings.newFeatures
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.newFeatures ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1944">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.newFeatures ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1945" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1946">
                    <div data-id="element-1947">
                      <div className="font-medium" data-id="element-1948">Marketing Emails</div>
                      <div className="text-sm text-gray-500" data-id="element-1949">
                        Receive promotional content and offers
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  marketingEmails: !notificationSettings.marketingEmails
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.marketingEmails ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1950">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.marketingEmails ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1951" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1952">
                    <div data-id="element-1953">
                      <div className="font-medium" data-id="element-1954">Weekly Digest</div>
                      <div className="text-sm text-gray-500" data-id="element-1955">
                        Get a weekly summary of your activity
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  weeklyDigest: !notificationSettings.weeklyDigest
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.weeklyDigest ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1956">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.weeklyDigest ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1957" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-1958">
                    <div data-id="element-1959">
                      <div className="font-medium" data-id="element-1960">Security Alerts</div>
                      <div className="text-sm text-gray-500" data-id="element-1961">
                        Important security notifications
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  securityAlerts: !notificationSettings.securityAlerts
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.securityAlerts ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-1962">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.securityAlerts ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1963" />
                    </button>
                  </label>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-1964">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-1965">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};